import { request, apiBase } from "@common/request";
import gaSC from "@common/utils/ga";
import { enablePush, isPushSupported } from "@common/events/push-notification";
import { setCouponCidCookie, couponPath } from "@common/utils/coupon-cid";
import { assetUrl } from "@common/utils/asset-url";

/**
 * The notification bell.
 *
 * Ported from the .in engine's @common/headers/banners.js, but the data path is
 * genuinely different and that is the whole story of this file:
 *
 *   .in            ONE call to /misc/dlv?eid=<pageId>&et=<pageType> returns a
 *                  mixed list, filtered client-side by a numeric BannerType:
 *                  13 = bottom sticky, 47 = promo popup, 48 = buzz bell.
 *
 *   International   banners are addressed by SLUG on the same /banners/type/
 *                   endpoint the pages already use, and the split is by WHERE
 *                   the fetch happens, not by a type number:
 *                     buzz-bell-banners  client-side  (this file)
 *                     promo-banner       server-side  (app/layout.tsx there,
 *                                                      the read model here)
 *
 * So only the bell is fetched here. `promo-banner` is server-rendered into a
 * <template> (views/shared/_promo-banner.ejs); promoBannerInit() below only
 * places it and runs .in's open / auto-close behaviour.
 *
 * The bottom sticky banner (.in's type 13) has no International equivalent at
 * all — there is no slug for it.
 */
const BUZZ_SLUG = "buzz-bell-banners";

/** Cookie name, format and lifetime copied from src/utils/guestNotifications.ts. */
const READ_COOKIE = "grabon_buzz_banners_read";
const READ_COOKIE_MAX_AGE = 24 * 60 * 60;

export function bannerInit() {
  const bells = [
    document.getElementById("gMNotify"),
    document.getElementById("gDNotify"),
  ].filter(Boolean);
  if (bells.length === 0 || !apiBase()) return;

  request
    .get(`${apiBase()}/banners/type/${BUZZ_SLUG}`)
    .then((response) => {
      const group = ((response && response.data) || []).find(
        (entry) => entry && entry.bannerType === BUZZ_SLUG,
      );
      render(bells, (group && group.banners) || []);
    })
    .catch(() => render(bells, []));
}

/** .in's timing: expand 5s after load, collapse 5s later. */
const PROMO_OPEN_DELAY = 5000;
const PROMO_OPEN_FOR = 5000;

/**
 * The promo popup, ported from .in's type-47 block. Appended into `main` as
 * .in does — the stylesheet's `main.g-mer .promo-banner` offsets need it there.
 *
 * One deliberate difference: once the visitor opens or closes it, the
 * auto-open/auto-close timers are cancelled. .in let the timer collapse a
 * banner the visitor had just opened.
 */
export function promoBannerInit() {
  const template = document.getElementById("gPromoBannerTpl");
  const main = document.querySelector("main");
  if (!template || !main) return;

  main.appendChild(template.content.cloneNode(true));
  const banner = document.getElementById("gPromoBanner");
  if (!banner) return;

  const timers = [];
  const cancelTimers = () => timers.forEach((id) => window.clearTimeout(id));

  timers.push(
    window.setTimeout(() => {
      banner.classList.add("active");
      timers.push(window.setTimeout(() => banner.classList.remove("active"), PROMO_OPEN_FOR));
    }, PROMO_OPEN_DELAY),
  );

  document.getElementById("gPromoBannerOpen")?.addEventListener("click", () => {
    cancelTimers();
    banner.classList.add("active");
    gaSC("Promo Banner", "Open");
  });
  document.getElementById("gPromoBannerClose")?.addEventListener("click", () => {
    cancelTimers();
    banner.classList.remove("active");
    gaSC("Promo Banner", "Close");
  });
}

function render(bells, banners) {
  const panel = buildPanel(banners);
  const top = document.querySelector(".g-top");
  if (top) top.appendChild(panel);

  updateUnreadCount(bells, banners);
  wireBellToggle(bells, panel);
  wireItemClicks(panel, bells, banners);
}

/**
 * The panel markup mirrors .in's `#gNotifyC` so the migrated stylesheet applies:
 * a heading with a back chevron, then either the list or the empty state.
 */
function buildPanel(banners) {
  const panel = document.createElement("div");
  panel.className = "g-notify-c";
  panel.id = "gNotifyC";

  const items = banners
    .map((banner, index) => {
      const couponId = readKeyOf(banner);
      const _coupon = banner?.coupons?.[0];
      const _title = _coupon?.generatedTemplateString;
      const _cpnTitle = _coupon?.title;
      const title = banner.title || "";
      if (!title) return "";
      return `
        <li>
          <a href="javascript:void(0)" title="${esc(title)}"
             class="go-cpn-show"
             data-ctype="bl_c" data-retype="c"
             data-goid="${esc(couponId || "")}"
             data-cid="${esc((banner.couponIds && banner.couponIds[0]) || "")}"
             data-cpnpstn="${index}"
             data-type="${couponId ? "cp" : "dl"}"
             data-href="${esc(banner.link || "")}"
          >
            <img src="${esc(assetUrl(banner.image))}" alt="${esc(title)}" loading="lazy" />
            <div>
              ${_title ? `<p class="bnr__p">${esc(_title)}</p>` : ``}
              <span class="bnr__span">${esc(_cpnTitle ?? title)}</span>
            </div>
          </a>
        </li>`;
    })
    .join("");

  const body = items
    ? `<ul class="list-unstyled">${items}</ul>`
    : `<div class="g-notify-empty"><img src="/assets/icons/alaram-bell.svg" alt="No Notifications" /><p>Notifications Not Found</p></div>`;

  panel.innerHTML = `
    <p>
      <span id="closeNotify">
        <svg xmlns="http://www.w3.org/2000/svg" width="10" height="16" viewBox="0 0 10 16"><path fill="#090606" stroke="#090606" d="M1.212 8.5l6.534 6.293c.287.276.752.276 1.039 0 .287-.275.287-.723 0-.998L2.769 8l6.015-5.794c.287-.276.287-.723 0-1-.286-.275-.752-.275-1.038 0L1.212 7.5c-.283.273-.283.728 0 1z" /></svg>
      </span>
      Notifications
    </p>
    ${body}`;

  return panel;
}

/** Unread = banners whose read key is not already in the cookie. */
function updateUnreadCount(bells, banners) {
  const read = getReadIds();
  const unread = banners.filter((banner) => {
    const key = readKeyOf(banner);
    return key && !read.has(key);
  }).length;

  bells.forEach((bell) => {
    bell.setAttribute("data-has-banners", String(unread > 0));
    const count = bell.querySelector(".g-notification-count");
    if (count) count.textContent = String(unread);
  });
}

function wireBellToggle(bells, panel) {
  const body = document.getElementById("gBody") || document.body;

  bells.forEach((bell) => {
    bell.addEventListener("click", () => {
      // .in positions the panel from the bell's own box, via two custom
      // properties the stylesheet reads.
      const rect = bell.getBoundingClientRect();
      body.style.setProperty(
        "--_g-notification-box-top",
        `${rect.top + rect.height}`,
      );
      body.style.setProperty(
        "--_g-notification-box-right",
        `${window.innerWidth - rect.right}`,
      );

      panel.classList.toggle("show");
      body.classList.toggle("gnotify-open");
      markVisibleAsRead(panel);
      gaSC("Bell Click");
      // The Next.js header: opening the bell also enables push when the
      // visitor has not decided yet — fire-and-forget, the panel opens
      // regardless. A no-op when push is unconfigured, unsupported or denied.
      if (isPushSupported() && Notification.permission === "default") enablePush();
    });
  });

  const close = panel.querySelector("#closeNotify");
  if (close) {
    close.addEventListener("click", () => {
      panel.classList.remove("show");
      body.classList.remove("gnotify-open");
    });
  }

  document.addEventListener("click", (event) => {
    if (panel.contains(event.target)) return;
    if (
      bells.some((bell) => bell.contains(event.target) || bell === event.target)
    )
      return;
    panel.classList.remove("show");
    body.classList.remove("gnotify-open");
  });
}

/**
 * A bell item opens the coupon page, same hand-off as everywhere else — the
 * cid cookie then the /coupon-codes/ page. `go-cpn-show` in the markup means
 * @common/events/coupon-click.js already handles the navigation and the click
 * tracking; this only records the read state.
 */
function wireItemClicks(panel, bells, banners) {
  panel.querySelectorAll("ul li a").forEach((link) => {
    link.addEventListener("click", () => {
      const key = (link.getAttribute("data-goid") || "").trim().toLowerCase();
      if (key) {
        const read = getReadIds();
        read.add(key);
        setReadIds(read);
      }
      updateUnreadCount(bells, banners);

      // No coupon on this banner: fall back to its plain link.
      const couponId = link.getAttribute("data-goid");
      if (!couponId) {
        const href = link.getAttribute("data-href");
        if (href) window.open(href, "_blank", "noopener");
      }
    });
  });
}

/** Opening the panel marks what is on screen as seen, like .in does. */
function markVisibleAsRead(panel) {
  panel.querySelectorAll("ul li a").forEach((link) => {
    const key = (link.getAttribute("data-goid") || "").trim().toLowerCase();
    link.setAttribute("data-clicked", String(!!key && getReadIds().has(key)));
  });
}

/**
 * The read key is the coupon id — `coupons[0].couponId` first, then
 * `couponIds[0]`, lowercased. Same rule as getBuzzBannerReadKey() in the
 * Next.js app, so a visitor's read state survives the cutover.
 */
function readKeyOf(banner) {
  const fromCoupons =
    banner.coupons && banner.coupons[0] && banner.coupons[0].couponId;
  const fromIds = banner.couponIds && banner.couponIds[0];
  return String(fromCoupons || fromIds || "")
    .trim()
    .toLowerCase();
}

function getReadIds() {
  try {
    const raw = document.cookie
      .split("; ")
      .find((row) => row.indexOf(`${READ_COOKIE}=`) === 0);
    if (!raw) return new Set();
    const parsed = JSON.parse(decodeURIComponent(raw.split("=")[1]));
    return new Set(
      Array.isArray(parsed)
        ? parsed.map((id) => String(id).trim().toLowerCase())
        : [],
    );
  } catch {
    return new Set();
  }
}

function setReadIds(ids) {
  const value = encodeURIComponent(JSON.stringify(Array.from(ids)));
  document.cookie = `${READ_COOKIE}=${value}; path=/; max-age=${READ_COOKIE_MAX_AGE}; SameSite=Lax`;
}

function esc(value) {
  return String(value == null ? "" : value).replace(
    /[&<>"']/g,
    (c) =>
      ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[
        c
      ],
  );
}

// Referenced so the shared hand-off helpers stay in one place even though
// coupon-click.js performs the navigation for `.go-cpn-show`.
export { setCouponCidCookie, couponPath };
